import { z } from "zod";

/**
 * The ports that can be switched between a mock and a real vendor (PLAN.md §4).
 * Lives here rather than in apps/api because `GET /health` reports the resolved
 * mode of each one, and the response schema is a client-facing contract.
 *
 * `workExecutor` and `clock` are deliberately absent: neither has a real
 * implementation to select (the work executors are model + tooling work, TODO(M10);
 * the clock is not an external call at all).
 */
export const ADAPTER_PORT_NAMES = [
  "llm",
  "guard",
  /**
   * Wave 33, EXPERIMENT: the System One model (TypeSafe's Jev, over
   * OpenRouter's decisions endpoint) that `NOB_JEV_MODE` switches parts of the
   * hearing onto. The FIFTH port a deployed tier may leave mocked, and the
   * easiest case of the rule that a mock must be a coherent product state: the
   * switch is `off` on every deployed tier by refusal (`environment.ts`), so a
   * mocked port there is a port nothing calls.
   */
  "systemOne",
  "embeddings",
  /**
   * Speech to text for voice notes (ruling R-0905-4): a sender's or an owner's
   * recording is transcribed at upload and the transcript is what the model
   * reads. xAI's `/v1/stt` behind the real adapter; the mock answers a
   * deterministic line a test can script.
   */
  "transcription",
  "payments",
  "mail",
  "storage",
  /**
   * M10 (2026-08-03): real document extraction; R-0905-11 (2026-09-06): real
   * claim verification, source research and the sender probe on xAI's
   * Responses API. "real" for this port means all of that.
   */
  "workExecutor",
  /**
   * The attachment scan (ruling R-0926-1 (2), wave 92): clamd over TCP, run in
   * our own cluster. It is NOT optional anywhere a URL is deployed — a mocked
   * scanner passes everything but a test string, so staging refuses it as
   * firmly as production does (`apps/api/src/environment.ts`).
   */
  "scanner",
  /**
   * R-0910-12 (2026-09-10): Nob's own guarded fetcher for a link a knocker
   * named. "real" means the page is actually opened, over the public internet
   * only; the mock answers what a test scripted and reaches nothing.
   */
  "pages",
  "xIdentity",
  /**
   * Live handle sync off X's Account Activity API (owner decision 2026-08-21).
   * The SECOND port a deployed tier may leave mocked, and for a different
   * reason from `chatChannels`: this one is blocked externally, on a
   * developer-app approval nobody has, and its mock IS the interim product —
   * handles refresh on sign-in and on the first knock that misses the cache.
   * The exemption is in `apps/api/src/environment.ts`.
   */
  "xSync",
  /**
   * The chat platforms an owner may talk to their doorman from (docs/channels.md).
   * The one port a deployed tier may deliberately leave mocked: a Nob with no
   * Telegram bot is a Nob missing a convenience, not a Nob missing a feature —
   * see the exemption in `apps/api/src/environment.ts`.
   */
  "chatChannels",
  /**
   * Product analytics and feature flags (ruling R-0912-1): self-hosted PostHog.
   * The THIRD port a deployed tier may leave mocked, and the plainest case of
   * the rule that a mock must be a coherent product state — a Nob whose events
   * go nowhere is a Nob nobody is measuring, not a Nob that mis-serves anyone.
   * It has to be, too: staging boots before the PostHog stack exists. The
   * exemption is in `apps/api/src/environment.ts`.
   */
  "analytics",
  /**
   * The help desk (wave 14a): self-hosted Chatwoot. The FOURTH port a deployed
   * tier may leave mocked, and it earns the place the same way `analytics`
   * does — a Nob whose in-app help form is mocked still answers everybody who
   * writes to support@nob.me, because that address is the desk's own inbox and
   * reaches it without passing through this process at all. The mock is
   * therefore a coherent product state (one road to support instead of two),
   * and staging has to boot before the Chatwoot stack and its token exist. The
   * exemption is in `apps/api/src/environment.ts`.
   */
  "support",
  /**
   * The legal line's voicemail (wave 133): Twilio's recording of a call to the
   * number the DMCA agent and the legal contact publish. The SIXTH port a
   * deployed tier may leave mocked — a Nob with no number published has no
   * voicemail to lose, and the route refuses every call unsigned either way
   * (`apps/api/src/environment.ts`).
   */
  "voicemail",
] as const;
export const AdapterPortName = z.enum(ADAPTER_PORT_NAMES);
export type AdapterPortName = z.infer<typeof AdapterPortName>;

export const AdapterMode = z.enum(["mock", "real"]);
export type AdapterMode = z.infer<typeof AdapterMode>;

export const HealthResponse = z.object({
  status: z.literal("ok"),
  /**
   * The AGGREGATE mode: "mock" only when every port resolved to a mock. It stays
   * a scalar because it is what an operator (and the e2e harness, which refuses
   * to drive anything but mocks) asks first — "is this a live process?".
   */
  adapters: AdapterMode,
  /** The resolved mode of every selectable port — what is actually live. */
  ports: z.record(AdapterPortName, AdapterMode),
  version: z.string(),
});
export type HealthResponse = z.infer<typeof HealthResponse>;
